import { t } from './sprache'

/**
 * Ein Eintrag der Agenda.
 *
 * @param id Die Kennung des Kapitels im Bildschirm — dorthin springt der Eintrag.
 * @param text Was in der Agenda steht: die Überschrift des Kapitels, nicht eine Umschreibung.
 * @param untergeordnet Ein Kapitel innerhalb eines Kapitels — eingerückt.
 */
export type Agendaeintrag = { id: string; text: string; untergeordnet?: boolean }

/**
 * Die Agenda eines langen Berichts (7.2 Abschnitt 19.5, R812).
 *
 * **Der Sprung klappt auf.** Ein Eintrag, der an einer zugeklappten
 * Überschrift endet, hat seine Aufgabe nicht erfüllt: Der Leser landet an
 * einem Wort und sieht nichts. Geöffnet wird das Kapitel **und jedes darüber**
 * — ein Werkzeug steckt im Beispielkonzern, und der kann selbst zu sein.
 *
 * **Zwei Schalter gehören dazu.** Wer sucht, klappt alles auf und benutzt die
 * Suche des Browsers; wer überblicken will, klappt alles zu und liest die
 * Überschriften. Beides ist ein Handgriff und nicht vierzehn.
 *
 * **Sie ist allgemein und gehört keinem Bericht.** Eine Tafel — Bilanz, GuV,
 * Journal — bekommt keine; die haben kein Kapitel. Der nächste Bericht aus
 * Abschnitten benutzt dieselbe Agenda.
 */
export function Agenda({ eintraege }: { eintraege: Agendaeintrag[] }) {
  return (
    <nav className="agenda" aria-label={t('agenda.titel')}>
      <h2>{t('agenda.titel')}</h2>

      <ol>
        {eintraege.map((e) => (
          <li key={e.id} className={e.untergeordnet ? 'tief' : undefined}>
            <a
              href={`#${e.id}`}
              onClick={(ereignis) => {
                ereignis.preventDefault()
                springeZu(e.id)
              }}
            >
              {e.text}
            </a>
          </li>
        ))}
      </ol>

      <div className="knopfreihe">
        <button type="button" onClick={() => alleKapitel(true)}>
          {t('agenda.alles-auf')}
        </button>
        <button type="button" onClick={() => alleKapitel(false)}>
          {t('agenda.alles-zu')}
        </button>
      </div>
    </nav>
  )
}

/**
 * Klappt jedes Kapitel des Arbeitsfeldes auf oder zu.
 *
 * **Am Baum und nicht am Zustand.** Ein aufklappbares Kapitel führt seinen
 * Zustand im Element selbst — so macht es die Anwendung überall, seit es den
 * ersten Vorgang zum Aufklappen gab. Eine zweite Buchführung darüber in React
 * wäre eine zweite Wahrheit, und der Anwender klappt ohnehin auch von Hand.
 */
function alleKapitel(offen: boolean) {
  for (const kapitel of document.querySelectorAll<HTMLDetailsElement>('.arbeitsfeld details')) {
    kapitel.open = offen
  }
}

/**
 * Springt zu einem Kapitel — und öffnet es samt allem, worin es steckt.
 */
function springeZu(id: string) {
  const ziel = document.getElementById(id)
  if (!ziel) {
    return
  }

  let kapitel: HTMLDetailsElement | null =
    ziel instanceof HTMLDetailsElement ? ziel : ziel.closest('details')

  while (kapitel) {
    kapitel.open = true
    kapitel = kapitel.parentElement?.closest('details') ?? null
  }

  // **jsdom kennt kein Rollen.** Die Prüfung soll das Aufklappen belegen und
  // nicht an einer Funktion scheitern, die es im Browser gibt und im Testlauf
  // nicht.
  ziel.scrollIntoView?.({ block: 'start' })
}
